import ExpandMore from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import { AppButton } from '@/components/atoms';
import { consultationCta, type NavItem } from '@/config/navigation';
import { FIT, fitMark } from '@/hooks/useHeaderFit';

import { OVERFLOW_MENU_LABEL } from './buildOverflowMenu';
import { headerCtaSx, secondaryCtaPx } from './headerCtaSx';
import { navItemSx, NAV_ROW_GAP } from './navItemSx';

/** Colour is irrelevant to a width; these keep `navItemSx` happy. */
const MEASURING_INK = 'transparent';

type Props = {
  /** The full primary nav, in row order — folded or not. */
  items: readonly NavItem[];
};

/**
 * A hidden, inert copy of the widest the header's nav row could ever be.
 *
 * `useHeaderFit` folds items away when they do not fit, which leaves it unable
 * to measure the very things it has to decide about: an item that is not on
 * the row has no width, and an item with no width looks like one that fits.
 * Measuring from a twin that always holds everything breaks that circle — the
 * natural width of every pill is readable at every moment, whatever the row is
 * currently showing, so the fold is computed from constants rather than from
 * the consequences of the last fold.
 *
 * It is also what makes the hook survive a web font. The labels are measured
 * after whatever font actually rendered them, not from a table of guesses, so
 * a late swap moves the twin, the observer fires, and the row is replanned.
 *
 * ## Why these are plain buttons and not the real components
 *
 * The real items are `NavMenuButton`s, each with hover timers, a `useId`, a
 * `matchPath` against the current route and a lazily fetched panel. Seven more
 * of those, mounted purely to be measured, would double that work and put a
 * second set of ids and effects in the tree for no visible result. What a
 * width depends on is the typography, the padding and the end icon, and those
 * come from the same `navItemSx` and `headerCtaSx` the real ones use — so the
 * twin matches by sharing the styling, not by duplicating the behaviour.
 *
 * ## Why it is safe
 *
 * `visibility: hidden` takes the whole subtree out of the accessibility tree
 * and out of the tab order, while — unlike `display: none` — leaving it
 * laid out and therefore measurable. `aria-hidden` and `tabIndex={-1}` say the
 * same thing twice on purpose, and `position: absolute` keeps it from taking
 * up a single pixel of the row it is describing.
 */
export function HeaderNavMeasure({ items }: Props) {
  return (
    <Box
      {...fitMark(FIT.ghost)}
      aria-hidden
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: 'max-content',
        display: 'flex',
        alignItems: 'center',
        gap: NAV_ROW_GAP,
        visibility: 'hidden',
        pointerEvents: 'none',
      }}
    >
      {items.map((item) => (
        <Button
          key={item.kind === 'link' ? item.to : item.label}
          {...fitMark(FIT.ghostItem)}
          tabIndex={-1}
          {...(item.kind === 'menu' && { endIcon: <ExpandMore /> })}
          sx={[navItemSx(MEASURING_INK, true), { '& .MuiButton-endIcon': { ml: 1 } }]}
        >
          {item.label}
        </Button>
      ))}
      <Button
        {...fitMark(FIT.ghostMore)}
        tabIndex={-1}
        endIcon={<ExpandMore />}
        sx={[navItemSx(MEASURING_INK, true), { '& .MuiButton-endIcon': { ml: 1 } }]}
      >
        {OVERFLOW_MENU_LABEL}
      </Button>
      <AppButton
        {...fitMark(FIT.ghostCta)}
        appVariant="secondary"
        tabIndex={-1}
        sx={[headerCtaSx, { px: secondaryCtaPx }]}
      >
        {consultationCta.label}
      </AppButton>
    </Box>
  );
}
